/**
 * TOSS Web Application - Calculator Hook
 *
 * Custom hook that provides calculator functionality with keyboard support.
 *
 * Features:
 * - Basic arithmetic operations (+, -, ×, ÷)
 * - Percentage calculation
 * - Sign toggle (+/-)
 * - Clear and backspace
 * - Expression history display
 * - Keyboard support
 * - Calculation history with localStorage persistence
 *
 * @example
 * ```tsx
 * function Calculator() {
 *   const {
 *     display,
 *     expression,
 *     history,
 *     inputDigit,
 *     inputOperation,
 *     calculate,
 *     clear,
 *     clearHistory,
 *     useHistoryItem,
 *   } = useCalculator();
 *
 *   return (
 *     <div>
 *       <div>{expression}</div>
 *       <div>{display}</div>
 *       <button onClick={() => inputDigit('1')}>1</button>
 *       <button onClick={() => inputOperation('+')}>+</button>
 *       <button onClick={calculate}>=</button>
 *       <button onClick={clear}>C</button>
 *       {history.map((item) => (
 *         <div key={item.id} onClick={() => useHistoryItem(item)}>
 *           {item.expression} = {item.result}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */

import { useState, useCallback, useEffect, useRef } from 'react';

// ============================================================================
// Types
// ============================================================================

export type Operation = '+' | '-' | '×' | '÷' | null;

export interface HistoryItem {
  id: string;
  expression: string;
  result: string;
  timestamp: number;
}

export interface CalculatorState {
  display: string;
  expression: string;
  previousValue: number | null;
  operation: Operation;
  waitingForOperand: boolean;
  shouldResetDisplay: boolean;
}

export interface UseCalculatorReturn {
  /** Current display value */
  display: string;
  /** Expression being calculated (e.g., "10 + 5") */
  expression: string;
  /** Calculation history */
  history: HistoryItem[];
  /** Input a digit (0-9) */
  inputDigit: (digit: string) => void;
  /** Input decimal point */
  inputDecimal: () => void;
  /** Input an operation (+, -, ×, ÷) */
  inputOperation: (op: '+' | '-' | '×' | '÷') => void;
  /** Calculate the result */
  calculate: () => void;
  /** Clear current entry */
  clear: () => void;
  /** Clear all (AC) */
  clearAll: () => void;
  /** Remove last digit */
  backspace: () => void;
  /** Toggle sign (+/-) */
  toggleSign: () => void;
  /** Calculate percentage */
  percentage: () => void;
  /** Clear calculation history */
  clearHistory: () => void;
  /** Use a history item result as current value */
  useHistoryItem: (item: HistoryItem) => void;
  /** Paste a number from clipboard */
  pasteNumber: (value: string) => void;
  /** Enable keyboard input for this element ref */
  containerRef: React.RefObject<HTMLDivElement | null>;
}

// ============================================================================
// Constants
// ============================================================================

const INITIAL_STATE: CalculatorState = {
  display: '0',
  expression: '',
  previousValue: null,
  operation: null,
  waitingForOperand: false,
  shouldResetDisplay: false,
};

const MAX_DISPLAY_LENGTH = 15;
const HISTORY_STORAGE_KEY = 'toss-calculator-history';
const MAX_HISTORY_ITEMS = 50;

// ============================================================================
// History Helper Functions
// ============================================================================

function loadHistory(): HistoryItem[] {
  try {
    const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return [];
}

function saveHistory(history: HistoryItem[]): void {
  try {
    // Keep only the most recent items
    const trimmed = history.slice(0, MAX_HISTORY_ITEMS);
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // Ignore storage errors
  }
}

function generateHistoryId(): string {
  return `calc-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatDisplay(value: number): string {
  // Handle special cases
  if (!isFinite(value)) {
    return 'Ошибка';
  }

  const stringValue = value.toString();

  // If the number is too long, use scientific notation
  if (stringValue.length > MAX_DISPLAY_LENGTH) {
    return value.toPrecision(10);
  }

  // Format with proper decimal places
  if (Number.isInteger(value)) {
    return value.toLocaleString('ru-RU');
  }

  // For decimals, limit decimal places and format
  const formatted = value.toLocaleString('ru-RU', {
    maximumFractionDigits: 10,
    minimumFractionDigits: 0,
  });

  return formatted.length > MAX_DISPLAY_LENGTH
    ? value.toPrecision(10)
    : formatted;
}

function parseDisplay(display: string): number {
  // Handle localized numbers (replace comma with dot, remove spaces)
  const normalized = display.replace(/\s/g, '').replace(',', '.');
  return parseFloat(normalized);
}

function performOperation(
  left: number,
  right: number,
  operation: Operation
): number {
  switch (operation) {
    case '+':
      return left + right;
    case '-':
      return left - right;
    case '×':
      return left * right;
    case '÷':
      return right !== 0 ? left / right : NaN;
    default:
      return right;
  }
}

// ============================================================================
// Hook
// ============================================================================

export function useCalculator(): UseCalculatorReturn {
  const [state, setState] = useState<CalculatorState>(INITIAL_STATE);
  const [history, setHistory] = useState<HistoryItem[]>(() => loadHistory());
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Save history to localStorage when it changes
  useEffect(() => {
    saveHistory(history);
  }, [history]);

  // Add item to history
  const addToHistory = useCallback((expression: string, result: string) => {
    const newItem: HistoryItem = {
      id: generateHistoryId(),
      expression,
      result,
      timestamp: Date.now(),
    };
    setHistory((prev) => [newItem, ...prev].slice(0, MAX_HISTORY_ITEMS));
  }, []);

  // Clear history
  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  // Use a history item
  const useHistoryItem = useCallback((item: HistoryItem) => {
    setState((prev) => ({
      ...prev,
      display: item.result,
      expression: '',
      previousValue: null,
      operation: null,
      waitingForOperand: false,
      shouldResetDisplay: true,
    }));
  }, []);

  // Input a digit
  const inputDigit = useCallback((digit: string) => {
    setState((prev) => {
      if (prev.waitingForOperand || prev.shouldResetDisplay) {
        return {
          ...prev,
          display: digit,
          waitingForOperand: false,
          shouldResetDisplay: false,
        };
      }

      // Don't allow leading zeros
      if (prev.display === '0' && digit === '0') {
        return prev;
      }

      // Replace leading zero with digit
      if (prev.display === '0') {
        return {
          ...prev,
          display: digit,
        };
      }

      // Check max length
      if (prev.display.replace(/[,.\s]/g, '').length >= MAX_DISPLAY_LENGTH) {
        return prev;
      }

      return {
        ...prev,
        display: prev.display + digit,
      };
    });
  }, []);

  // Input decimal point
  const inputDecimal = useCallback(() => {
    setState((prev) => {
      if (prev.waitingForOperand || prev.shouldResetDisplay) {
        return {
          ...prev,
          display: '0,',
          waitingForOperand: false,
          shouldResetDisplay: false,
        };
      }

      // Don't add decimal if already present
      if (prev.display.includes(',') || prev.display.includes('.')) {
        return prev;
      }

      return {
        ...prev,
        display: prev.display + ',',
      };
    });
  }, []);

  // Input operation
  const inputOperation = useCallback((op: '+' | '-' | '×' | '÷') => {
    setState((prev) => {
      const currentValue = parseDisplay(prev.display);

      // If we already have a pending operation, calculate it first
      if (prev.previousValue !== null && prev.operation && !prev.waitingForOperand) {
        const result = performOperation(prev.previousValue, currentValue, prev.operation);
        const formattedResult = formatDisplay(result);

        return {
          display: formattedResult,
          expression: `${formattedResult} ${op}`,
          previousValue: result,
          operation: op,
          waitingForOperand: true,
          shouldResetDisplay: false,
        };
      }

      // Format expression
      const displayForExpression = prev.display.replace('.', ',');

      return {
        ...prev,
        expression: `${displayForExpression} ${op}`,
        previousValue: currentValue,
        operation: op,
        waitingForOperand: true,
        shouldResetDisplay: false,
      };
    });
  }, []);

  /**
   * Calculate result.
   *
   * Reads `state` from the closure instead of an updater, and that is the whole point: the
   * history entry used to be queued with `setTimeout` from INSIDE `setState((prev) => …)`.
   * React calls an updater twice under `StrictMode`, so every «=» queued two timers and the
   * list got the same line twice — with different ids, so nothing deduplicated it later.
   * An updater must be pure; the write to history belongs out here, next to the state write.
   */
  const calculate = useCallback(() => {
    if (state.previousValue === null || !state.operation) {
      return;
    }

    const currentValue = parseDisplay(state.display);
    const result = performOperation(state.previousValue, currentValue, state.operation);
    const formattedResult = formatDisplay(result);

    // Format expression with result
    const displayForExpression = state.display.replace('.', ',');
    const fullExpression = `${state.expression} ${displayForExpression}`;

    setState({
      display: formattedResult,
      expression: `${fullExpression} =`,
      previousValue: null,
      operation: null,
      waitingForOperand: false,
      shouldResetDisplay: true,
    });

    addToHistory(fullExpression, formattedResult);
  }, [state, addToHistory]);

  // Clear current entry
  const clear = useCallback(() => {
    setState((prev) => ({
      ...prev,
      display: '0',
    }));
  }, []);

  // Clear all
  const clearAll = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  // Backspace
  const backspace = useCallback(() => {
    setState((prev) => {
      if (prev.waitingForOperand || prev.shouldResetDisplay) {
        return prev;
      }

      if (prev.display.length === 1) {
        return {
          ...prev,
          display: '0',
        };
      }

      return {
        ...prev,
        display: prev.display.slice(0, -1),
      };
    });
  }, []);

  // Toggle sign
  const toggleSign = useCallback(() => {
    setState((prev) => {
      const currentValue = parseDisplay(prev.display);
      const newValue = currentValue * -1;
      return {
        ...prev,
        display: formatDisplay(newValue),
      };
    });
  }, []);

  // Percentage
  const percentage = useCallback(() => {
    setState((prev) => {
      const currentValue = parseDisplay(prev.display);

      // If we have a pending operation, calculate percentage of the previous value
      if (prev.previousValue !== null && (prev.operation === '+' || prev.operation === '-')) {
        const percentValue = prev.previousValue * (currentValue / 100);
        return {
          ...prev,
          display: formatDisplay(percentValue),
        };
      }

      // Otherwise, just divide by 100
      const percentValue = currentValue / 100;
      return {
        ...prev,
        display: formatDisplay(percentValue),
      };
    });
  }, []);

  // Paste number
  const pasteNumber = useCallback((value: string) => {
    // Clean and validate the pasted value
    const cleaned = value
      .replace(/\s/g, '')      // Remove spaces
      .replace(/,/g, '.')      // Replace comma with dot
      .replace(/[^0-9.-]/g, ''); // Keep only digits, dot, and minus

    // Parse as number to validate
    const num = parseFloat(cleaned);
    if (!isNaN(num) && isFinite(num)) {
      setState((prev) => ({
        ...prev,
        display: formatDisplay(num),
        waitingForOperand: false,
        shouldResetDisplay: false,
      }));
    }
  }, []);

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if focus is in an input field
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // Check if our container is mounted and focused
      if (
        !containerRef.current ||
        !containerRef.current.contains(document.activeElement)
      ) {
        return;
      }

      const key = e.key;

      // Digits
      if (/^[0-9]$/.test(key)) {
        e.preventDefault();
        inputDigit(key);
        return;
      }

      // Operations
      switch (key) {
        case '+':
          e.preventDefault();
          inputOperation('+');
          break;
        case '-':
          e.preventDefault();
          inputOperation('-');
          break;
        case '*':
          e.preventDefault();
          inputOperation('×');
          break;
        case '/':
          e.preventDefault();
          inputOperation('÷');
          break;
        case 'Enter':
        case '=':
          e.preventDefault();
          calculate();
          break;
        case '.':
        case ',':
          e.preventDefault();
          inputDecimal();
          break;
        case 'Backspace':
          e.preventDefault();
          backspace();
          break;
        case 'Escape':
          e.preventDefault();
          clearAll();
          break;
        case '%':
          e.preventDefault();
          percentage();
          break;
        case 'v':
        case 'V':
          // Handle Ctrl+V / Cmd+V paste
          if (e.ctrlKey || e.metaKey) {
            // Don't prevent default, let the paste event handle it
          }
          break;
      }
    };

    const handlePaste = (e: ClipboardEvent) => {
      // Check if our container is mounted and focused
      if (
        !containerRef.current ||
        !containerRef.current.contains(document.activeElement)
      ) {
        return;
      }

      const pastedText = e.clipboardData?.getData('text');
      if (pastedText) {
        e.preventDefault();
        pasteNumber(pastedText);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('paste', handlePaste);
    };
  }, [inputDigit, inputDecimal, inputOperation, calculate, backspace, clearAll, percentage, pasteNumber]);

  return {
    display: state.display,
    expression: state.expression,
    history,
    inputDigit,
    inputDecimal,
    inputOperation,
    calculate,
    clear,
    clearAll,
    backspace,
    toggleSign,
    percentage,
    clearHistory,
    useHistoryItem,
    pasteNumber,
    containerRef,
  };
}

export default useCalculator;
